/**
 * @copyright Copyright 2021-2023 Epic Systems Corporation
 * @file Nominated Apps Buttons (used by the Nominated Apps widget)
 * @module Epic.AppOrchard.Dashboard.NominatedAppsButtons
 */

import { VStack } from "@chakra-ui/react";
import { AOLink, ContactButton } from "ao/components/Core";
import { WidgetActionButton } from "ao/components/Dashboard/widgetHelpers";
import { contactAuthor } from "ao/data";
import { IAppWidgetActionButtonProps } from "ao/types/dashboard";
import React, { FC, memo } from "react";
import { BiCheckCircle } from "react-icons/bi";
import { MarkNowImplementingNowButton } from ".";

interface INominatedAppsButtonsProps extends IAppWidgetActionButtonProps {
	appLinkRef: React.RefObject<HTMLAnchorElement> | null;
	downloadPageUrl: string;
	isGalleryEnabled: boolean | undefined;
}

/**  Nominated Apps Buttons (used by the Nominated Apps widget) */
export const NominatedAppsButtons: FC<INominatedAppsButtonsProps> = memo(
	(props: INominatedAppsButtonsProps) => {
		const { app, dispatch, orgId, appLinkRef, downloadPageUrl, isGalleryEnabled } = props;

		return (
			<VStack w="100%" spacing="0.25em">
				<WidgetActionButton
					colorScheme="green"
					leftIcon={<BiCheckCircle size={20} />}
					url={downloadPageUrl}
					target="blank"
					title="Open this app's listing"
					as={AOLink}
					textDecoration="none"
					_hover={{
						textDecoration: "none",
						background: "green.50",
						color: "green",
					}}
				>
					Download
				</WidgetActionButton>
				{isGalleryEnabled && (
					<ContactButton
						buttonAs={WidgetActionButton}
						contactApi={contactAuthor}
						apiArgs={[app.appId]}
						buttonCaption="Contact"
						finalFocusRef={appLinkRef || undefined}
						title={`Let ${app.appOrgName} know you're interested in ${app.appName}`}
						buttonNotContactedHelptext="Send an email to the vendor indicating your interest and inviting them to start a conversation with you."
						successMessage="An email was sent to the vendor."
						errorMessage="Error contacting the vendor. Please try again later or contact your Epic representative if this continues to happen."
						confirmModalTitle={`Contact ${app.appOrgName ?? app.appName}`}
						confirmModalPrompt={`This will send an email to ${
							app.appOrgName ?? app.appName
						} indicating your interest and inviting them to start a conversation with you. Are you sure you want to continue?`}
					/>
				)}
				<MarkNowImplementingNowButton app={app} orgId={orgId} dispatch={dispatch} />
			</VStack>
		);
	},
);
